'use client'

import { useState, useCallback } from 'react'
import html2canvas from 'html2canvas'

interface AIAnalysisState {
  isModalOpen: boolean
  loading: boolean
  error: string | null
  analysis: string | null
}

interface UseAIAnalysisProps {
  symbol: string
  timeframe: string
  currentPrice?: number
}

export const useAIAnalysis = ({ symbol, timeframe, currentPrice }: UseAIAnalysisProps) => {
  const [state, setState] = useState<AIAnalysisState>({
    isModalOpen: false,
    loading: false,
    error: null,
    analysis: null
  })

  const captureChartScreenshot = useCallback(async (chartContainer: HTMLElement): Promise<string> => {
    try {
      // Wait a bit for any ongoing chart animations to complete
      await new Promise(resolve => setTimeout(resolve, 500))

      const canvas = await html2canvas(chartContainer, {
        backgroundColor: '#1a1a2e', // Dark background
        width: chartContainer.scrollWidth,
        height: chartContainer.scrollHeight,
        scale: 1, // Reduce scale for faster processing while maintaining quality
        useCORS: true,
        allowTaint: false,
        removeContainer: true,
        logging: false,
        onclone: (clonedDoc: Document) => {
          // Ensure all chart elements are visible in the clone
          const clonedContainer = clonedDoc.querySelector('[data-chart-container]') as HTMLElement
          if (clonedContainer) {
            clonedContainer.style.transform = 'none'
            clonedContainer.style.overflow = 'visible'
          }
        }
      })

      return canvas.toDataURL('image/png', 0.8) // Compress image for faster upload
    } catch (error) {
      console.error('Screenshot capture failed:', error)
      throw new Error('Failed to capture chart screenshot')
    }
  }, [])

  const analyzeChart = useCallback(async (chartContainer: HTMLElement) => {
    setState(prev => ({
      ...prev,
      isModalOpen: true,
      loading: true,
      error: null,
      analysis: null
    }))

    try {
      // Capture screenshot
      const screenshot = await captureChartScreenshot(chartContainer)

      // Send to AI analysis API
      const response = await fetch('/api/chart/ai-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: screenshot,
          symbol,
          timeframe,
          currentPrice
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Analysis request failed')
      }

      const data = await response.json()
      
      setState(prev => ({
        ...prev,
        loading: false,
        analysis: data.analysis
      }))

    } catch (error) {
      console.error('AI Analysis failed:', error)
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Analysis failed. Please try again.'
      }))
    }
  }, [symbol, timeframe, currentPrice, captureChartScreenshot])

  const retryAnalysis = useCallback(async (chartContainer: HTMLElement) => {
    await analyzeChart(chartContainer)
  }, [analyzeChart])

  const closeModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isModalOpen: false,
      // Keep analysis data so user can reopen modal to view results
    }))
  }, [])

  const openModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isModalOpen: true
    }))
  }, [])

  return {
    isModalOpen: state.isModalOpen,
    loading: state.loading,
    error: state.error,
    analysis: state.analysis,
    analyzeChart,
    retryAnalysis,
    closeModal,
    openModal
  }
} 